import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";

import { formatEquipmentLabel } from "../../utils/formatEquipment";
import { EquipmentsRecord } from "../../interfaces/Equipment.interface";

type Counts = {
  _25: number;
  _50: number;
  _75: number;
  _100: number;
  _125: number;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    items: Array<{
      site: string;
      equipment: string;
      counts: Counts;
      note?: string;
      scheduledAt?: string;
      providerDidNotPass?: boolean;
    }>;
  }) => Promise<void> | void;
  certified: boolean;
  setCertified: (v: boolean) => void;
  equipment: EquipmentsRecord;
  siteId: string;
  collectionDate?: string | Date;
}

const LEVELS: Array<{ key: keyof Counts; label: string; weight: number }> = [
  { key: "_25", label: "25%", weight: 0.25 },
  { key: "_50", label: "50%", weight: 0.5 },
  { key: "_75", label: "75%", weight: 0.75 },
  { key: "_100", label: "100%", weight: 1.0 },
  { key: "_125", label: "125% (débordement)", weight: 1.25 },
];

// petit slider natif stylé
const RangeSlider: React.FC<{
  value: number;
  max: number;
  disabled?: boolean;
  onChange: (val: number) => void;
}> = ({ value, max, disabled, onChange }) => {
  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        disabled={disabled || max === 0}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: "100%",
          accentColor: "#1976d2", // compatible chrome/edge
          cursor: disabled || max === 0 ? "not-allowed" : "pointer",
        }}
      />
    </Box>
  );
};

export const SingleEquipmentRemovalDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  certified,
  setCertified,
  equipment,
  siteId,
  collectionDate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [note, setNote] = React.useState("");
  const [providerDidNotPass, setProviderDidNotPass] = React.useState(false);
  const [counts, setCounts] = React.useState<Counts>({
    _25: 0,
    _50: 0,
    _75: 0,
    _100: 0,
    _125: 0,
  });

  React.useEffect(() => {
    if (open) {
      setCounts({ _25: 0, _50: 0, _75: 0, _100: 0, _125: 0 });
      setNote("");
      setProviderDidNotPass(false);
    }
  }, [open]);

  const quantity = equipment?.quantity ?? 0;

  const totalWithout = (omitKey?: keyof Counts) =>
    LEVELS.reduce(
      (acc, { key }) => acc + (key === omitKey ? 0 : counts[key] || 0),
      0
    );

  const sliderMax = (key: keyof Counts) => {
    const others = totalWithout(key);
    const m = Math.max(0, quantity - others);
    return Number.isFinite(m) ? m : 0;
  };

  const sumAll = totalWithout(undefined);

  const eqFull = (() => {
    let sum = 0;
    for (const { key, weight } of LEVELS) {
      sum += (counts[key] || 0) * weight;
    }
    return sum;
  })();

  const overInstalled = sumAll > quantity;

  const canSubmit =
    certified &&
    !overInstalled &&
    (!providerDidNotPass ? sumAll > 0 : true);

  const setSlider = (key: keyof Counts, val: number) => {
    const max = sliderMax(key);
    const v = Math.min(Math.max(0, Math.round(val)), max);
    setCounts((prev) => ({ ...prev, [key]: v }));
  };

  const handleSend = async () => {
    if (!providerDidNotPass && sumAll > quantity) return;

    const payloadItem = {
      site: siteId,
      equipment: equipment._id!,
      counts: {
        _25: counts._25 || 0,
        _50: counts._50 || 0,
        _75: counts._75 || 0,
        _100: counts._100 || 0,
        _125: counts._125 || 0,
      },
      note: note || undefined,
      scheduledAt: collectionDate
        ? collectionDate instanceof Date
          ? collectionDate.toISOString()
          : new Date(collectionDate).toISOString()
        : undefined,
      providerDidNotPass,
    };

    if (providerDidNotPass) {
      payloadItem.counts = { _25: 0, _50: 0, _75: 0, _100: 0, _125: 0 };
    }

    await onSubmit({ items: [payloadItem] });
  };

  const headerLabel = (() => {
    const base = formatEquipmentLabel(equipment);
    if (!collectionDate) return base;
    const d = new Date(collectionDate);
    return `${base} — ${d.toLocaleDateString("fr-FR")}`;
  })();

  const DesktopTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Niveau</TableCell>
          <TableCell>Curseur</TableCell>
          <TableCell align="right">Valeur</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {LEVELS.map(({ key, label }) => {
          const max = sliderMax(key);
          return (
            <TableRow key={key}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2">
                  {label} {providerDidNotPass ? "(désactivé)" : ""}
                  {max === 0 && !providerDidNotPass ? " (plus de dispo)" : ""}
                </Typography>
              </TableCell>
              <TableCell sx={{ width: "100%" }}>
                <RangeSlider
                  value={counts[key]}
                  max={max}
                  disabled={providerDidNotPass}
                  onChange={(v) => setSlider(key, v)}
                />
              </TableCell>
              <TableCell align="right" sx={{ minWidth: 80 }}>
                <Typography variant="body2">
                  {counts[key]}/{max}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const MobileList = () => (
    <Stack spacing={1.5}>
      {LEVELS.map(({ key, label }) => {
        const max = sliderMax(key);
        return (
          <Box
            key={key}
            sx={{
              p: 1.2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.default",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" fontWeight={500}>
                {label}
              </Typography>
              <Chip
                size="small"
                label={`${counts[key]}/${max}`}
                color={counts[key] > 0 ? "primary" : "default"}
              />
            </Stack>

            <RangeSlider
              value={counts[key]}
              max={max}
              disabled={providerDidNotPass}
              onChange={(v) => setSlider(key, v)}
            />

            {providerDidNotPass && (
              <Typography variant="caption" color="text.secondary">
                Désactivé car le prestataire n'est pas passé
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Déclarer un enlèvement — {headerLabel}</DialogTitle>
      <DialogContent dividers>
        {!equipment ? (
          <Alert severity="warning">Équipement introuvable.</Alert>
        ) : (
          <>
            <Stack spacing={2} sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={providerDidNotPass}
                    onChange={(e) => setProviderDidNotPass(e.target.checked)}
                  />
                }
                label="Le prestataire n'est pas passé"
              />
              <Typography variant="body2" color="text.secondary">
                Renseignez le nombre de contenants <strong>remplis</strong> à
                chaque niveau (25%, 50%, 75%, 100%, 125%). La somme ne peut pas
                dépasser la quantité installée ({quantity}).
              </Typography>
            </Stack>

            <Card
              variant="outlined"
              sx={{ borderColor: sumAll > quantity ? "error.main" : undefined }}
            >
              <CardHeader
                title={
                  <Typography variant="subtitle1">
                    {formatEquipmentLabel(equipment)}
                  </Typography>
                }
                subheader={
                  <Typography color="text.secondary">
                    Qté installée : {equipment.quantity}
                  </Typography>
                }
              />
              <CardContent>
                {isMobile ? <MobileList /> : <DesktopTable />}

                <Grid container sx={{ mt: 2 }} spacing={1}>
                  <Grid >
                    <Typography
                      variant="body2"
                      color={overInstalled ? "error.main" : "text.secondary"}
                    >
                      Total déclaré : <strong>{sumAll}</strong> / {quantity}
                      {overInstalled ? " (dépassement non autorisé)" : ""}
                    </Typography>
                  </Grid>
                  <Grid >
                    <Typography variant="body2">
                      Équiv. plein : <strong>{eqFull.toFixed(2)}</strong>
                      {counts._125 > 0 ? " (inclut débordement 125%)" : ""}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <TextField
              label="Note (optionnel)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
            />

            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "flex-start" : "center"}
              justifyContent="space-between"
              sx={{ mt: 2, gap: 1.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={certified}
                    onChange={(e) => setCertified(e.target.checked)}
                  />
                }
                label="Je certifie l'exactitude des informations fournies."
              />
              <Typography variant="body2" color="text.secondary">
                L'équivalent “plein” inclut le débordement à 125% si renseigné.
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} fullWidth={isMobile}>
          Annuler
        </Button>
        <Button
          onClick={handleSend}
          disabled={!canSubmit}
          variant="contained"
          fullWidth={isMobile}
        >
          Envoyer ma déclaration
        </Button>
      </DialogActions>
    </Dialog>
  );
};
